/**
 * useNetworkDebug - Hook for BuildApkPage
 *
 * Manages network debug info polling and debug log state.
 */
import { useState, useEffect } from 'react';

export interface NetworkInfo {
  zerotier?: {
    detected: boolean;
    ip?: string;
    interface?: string;
  };
  local_network: {
    ips: string[];
    primary: string;
  };
  server: {
    port_8001_open: boolean;
    hostname: string;
    recommended_url: string;
  };
  instructions: {
    mobile_app: string;
    network_type: string;
  };
}

export interface UseNetworkDebugReturn {
  debugLog: string;
  networkInfo: NetworkInfo | null;
  autoRefresh: boolean;
  setAutoRefresh: (value: boolean) => void;
  setDebugLog: (value: string) => void;
  fetchNetworkDebug: () => Promise<void>;
}

export const useNetworkDebug = (): UseNetworkDebugReturn => {
  const [debugLog, setDebugLog] = useState<string>('🔧 Monitor de Rede Iniciado\n');
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchNetworkDebug = async () => {
    try {
      const response = await fetch('http://localhost:8001/api/network-debug');
      const data = await response.json();

      if (response.ok) {
        setNetworkInfo(data);

        const timestamp = new Date().toLocaleTimeString();
        let log = `\n[${timestamp}] 📡 Status de Rede\n`;
        log += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;

        if (data.zerotier?.detected) {
          log += `✅ ZeroTier: ATIVO\n`;
          log += `   IP: ${data.zerotier.ip}\n`;
          log += `   Interface: ${data.zerotier.interface}\n`;
        } else {
          log += `⚠️  ZeroTier: NÃO DETECTADO\n`;
        }

        log += `\n📍 Rede Local:\n`;
        log += `   IPs: ${data.local_network.ips.join(', ')}\n`;
        log += `   Primário: ${data.local_network.primary}\n`;

        log += `\n🔌 Servidor:\n`;
        log += `   Porta 8001: ${data.server.port_8001_open ? '✅ ABERTA' : '❌ FECHADA'}\n`;
        log += `   Hostname: ${data.server.hostname}\n`;
        log += `   URL Recomendada: ${data.server.recommended_url}\n`;

        log += `\n📱 Instruções para Celular:\n`;
        log += `   ${data.instructions.mobile_app}\n`;
        log += `   Tipo de Rede: ${data.instructions.network_type}\n`;

        setDebugLog(prev => prev + log + '\n');
      }
    } catch (error) {
      const timestamp = new Date().toLocaleTimeString();
      setDebugLog(prev => prev + `\n[${timestamp}] ❌ Erro ao buscar info de rede: ${error}\n`);
    }
  };

  useEffect(() => {
    fetchNetworkDebug();

    if (autoRefresh) {
      const interval = setInterval(fetchNetworkDebug, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  return {
    debugLog,
    networkInfo,
    autoRefresh,
    setAutoRefresh,
    setDebugLog,
    fetchNetworkDebug,
  };
};
